import { ZodError } from 'zod'

import { UnauthenticatedApi } from '@mondough/public-api-request'

import * as Api from './api'
// Reimport required to be able to mock the exported isBrowserRuntime
// see https://stackoverflow.com/a/55193363
import { isBrowserRuntime as isBrowser } from './server'
import type { CopyBundle, CopyBundleWithMetadata, OAuthClient } from './types'

export type { CopyBundle, CopyBundleWithMetadata, OAuthClient } from './types'

type CacheEntry = {
  data: CopyBundle | null
  timestamp: number
  runtimeOverlayVersion?: string
  updatedTimestamp?: string
}

const cache = new Map<string, CacheEntry>()

// Prevents thundering herd: concurrent requests share a single in-flight fetch
// rather than each making separate API calls
const inFlight = new Map<string, Promise<CopyBundle | null>>()

const CACHE_DURATION_MS = 5 * 60 * 1000 // 5 minutes

function getCacheKey(appName: string): string {
  return `${appName}-copy-v1`
}

type LoadCopyOptions = {
  forceRefresh?: boolean
}

/**
 * Server-side loader for fetching the specified copy bundle from `s.api.copy`
 */
export async function loadCopyServerSide(
  appName: string,
  client: OAuthClient,
  options: LoadCopyOptions = {},
): Promise<CopyBundle | null> {
  const result = await loadCopyServerSideWithMetadata(appName, client, options)
  return result?.bundle ?? null
}

/**
 * Server-side loader that returns the bundle with metadata for cache validation.
 * Use this when you need to check `updatedTimestamp` to determine if copy has changed.
 */
export async function loadCopyServerSideWithMetadata(
  appName: string,
  client: OAuthClient,
  options: LoadCopyOptions = {},
): Promise<CopyBundleWithMetadata | null> {
  if (isBrowser()) {
    return null
  }

  const apiHost = client.apiHost.endsWith('/')
    ? client.apiHost.slice(0, -1)
    : client.apiHost

  const cacheKey = getCacheKey(appName)
  const now = Date.now()
  const cached = cache.get(cacheKey)

  if (
    !options.forceRefresh &&
    cached &&
    cached.data &&
    cached.runtimeOverlayVersion &&
    cached.updatedTimestamp &&
    now - cached.timestamp < CACHE_DURATION_MS
  ) {
    console.info('[lib.copy] Serving from cache')
    return {
      bundle: cached.data,
      runtimeOverlayVersion: cached.runtimeOverlayVersion,
      updatedTimestamp: cached.updatedTimestamp,
    }
  }

  const existingRequest = inFlight.get(cacheKey)
  if (existingRequest) {
    console.info('[lib.copy] Waiting for in-flight request')
    const bundle = await existingRequest
    const cachedEntry = cache.get(cacheKey)
    if (
      bundle &&
      cachedEntry?.runtimeOverlayVersion &&
      cachedEntry?.updatedTimestamp
    ) {
      return {
        bundle,
        runtimeOverlayVersion: cachedEntry.runtimeOverlayVersion,
        updatedTimestamp: cachedEntry.updatedTimestamp,
      }
    }
    return null
  }

  const fetchPromise = fetchCopyBundle(appName, apiHost, client, cacheKey)
  inFlight.set(cacheKey, fetchPromise)

  try {
    const bundle = await fetchPromise
    const cachedEntry = cache.get(cacheKey)
    if (
      bundle &&
      cachedEntry?.runtimeOverlayVersion &&
      cachedEntry?.updatedTimestamp
    ) {
      return {
        bundle,
        runtimeOverlayVersion: cachedEntry.runtimeOverlayVersion,
        updatedTimestamp: cachedEntry.updatedTimestamp,
      }
    }
    return null
  } finally {
    inFlight.delete(cacheKey)
  }
}

async function fetchCopyBundle(
  appName: string,
  apiHost: string,
  client: OAuthClient,
  cacheKey: string,
): Promise<CopyBundle | null> {
  console.info(
    `[lib.copy] Attempting to fetch bundle '${appName}' from ${apiHost}`,
  )

  const appNameFromEnv = process.env.NEXT_PUBLIC_SERVICE_NAME
  if (appNameFromEnv && appName !== appNameFromEnv) {
    console.warn(
      `[lib.copy] Found an appName environment variable (NEXT_PUBLIC_SERVICE_NAME) which is different to the value supplied to loadCopyServerSide. NEXT_PUBLIC_SERVICE_NAME='${appNameFromEnv}' vs. appName='${appName}'. Double check that you're supplying the correct value, and favour the NEXT_PUBLIC_SERVICE_NAME env variable if you can.`,
    )
  }

  try {
    const api = new UnauthenticatedApi(apiHost)
    const tokenResponse = await Api.getClientToken(api, client)
    console.info('[lib.copy] Acquired oauth2 access_token')

    const response = await Api.getCopyBundle(
      api,
      appName,
      tokenResponse.access_token,
    )

    if (response.bundle === null) {
      console.info(`[lib.copy] No copy in response (bundle = null)`)
      return null
    }

    console.info(
      `[lib.copy] Successfully fetched bundle (containing ${getNumberOfKeysString(
        Object.keys(response.bundle).length,
      )})`,
    )

    cache.set(cacheKey, {
      data: response.bundle,
      timestamp: Date.now(),
      runtimeOverlayVersion: response.runtime_overlay_version,
      updatedTimestamp: response.updated_timestamp,
    })
    return response.bundle
  } catch (err) {
    const msg =
      err instanceof ZodError || err instanceof Error ? err.message : err
    console.warn('[lib.copy] Failed to load copy.', msg)
    return null
  }
}

// private helpers

function getNumberOfKeysString(numOfKeys: number) {
  return numOfKeys === 1 ? `${numOfKeys} key` : `${numOfKeys} keys`
}

export function isBrowserRuntime() {
  return (
    typeof window !== 'undefined' ||
    typeof document !== 'undefined' ||
    typeof process === 'undefined'
  )
}
